/**
 * ConnectionStatus - WebSocket connection indicator
 * Only shows when there's a connection issue
 */
import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';

const ConnectionStatus = ({ isConnected, error, onReconnect }) => {
  const [showStatus, setShowStatus] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  // Only show when disconnected for more than 3 seconds
  useEffect(() => {
    let timeout;
    if (!isConnected) {
      timeout = setTimeout(() => {
        setShowStatus(true);
      }, 3000);
    } else {
      setShowStatus(false);
      setIsReconnecting(false);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isConnected]);

  const handleReconnect = () => {
    setIsReconnecting(true);
    if (onReconnect) onReconnect();
    
    // Reset reconnecting state after 5 seconds if still not connected
    setTimeout(() => {
      setIsReconnecting(false);
    }, 5000);
  };

  // Don't render if connected or within grace period
  if (!showStatus) return null;

  return (
    <div 
      className="fixed bottom-4 right-4 z-50 animate-slide-up"
      data-testid="connection-status"
    >
      <div className="bg-slate-900 border border-red-600/50 rounded-lg shadow-xl p-4 max-w-xs">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-red-600/20 rounded-full">
            <WifiOff className="h-5 w-5 text-red-400" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-red-400">
              Connection Lost
            </h4>
            <p className="text-xs text-slate-400 mt-1">
              Real-time updates paused. The app will reconnect automatically.
            </p>
            <button
              onClick={handleReconnect}
              disabled={isReconnecting}
              className={`mt-3 text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-colors ${
                isReconnecting 
                  ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isReconnecting ? 'animate-spin' : ''}`} />
              {isReconnecting ? 'Reconnecting...' : 'Retry Now'}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ConnectionStatus;
